import { Button } from '@/components/ui/primitives';
import {
    MarketingPage,
    Hero,
    ContextNote,
    FeatureGrid,
    FAQList,
    CtaBand,
    PageFooter,
    Dateline,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';

const PAGE = pageForRoute('/bluebeam-for-mac');

const CHIPS = ['Works in Safari and Chrome', 'No install needed', 'Nothing uploaded', 'Free'] as const;

const FEATURE_CARDS = [
    {
        title: 'Browser-native, truly',
        body: 'Runs directly in Safari, Chrome, or Firefox on any Mac -- no Parallels, no Windows VM, no remote desktop. Open the URL and start marking up.',
    },
    {
        title: 'Full markup toolset',
        body: 'Lines, polylines, polygons, clouds, callouts, text, arrows, highlights, dimensions, and freehand -- every annotation type your redline workflow needs.',
    },
    {
        title: 'Measurement to scale',
        body: 'Calibrate to your drawing\'s scale and measure length, area, and perimeter for takeoffs. Imperial and metric, on any sheet size.',
    },
    {
        title: 'Stays on your machine',
        body: 'Your PDF never leaves your Mac. Everything -- markup, storage, export -- runs locally in your browser. No cloud upload, no account required.',
        accent: true,
    },
    {
        title: 'bim data overlay',
        body: 'Open a bim pdf to see element properties and model data directly on the drawing sheet. No separate viewer, no round trip.',
    },
    {
        title: 'Export a finished PDF',
        body: 'Flatten your markups into a stamped PDF, or export annotations to CSV. Standard formats, no proprietary lock-in.',
    },
] as const;

const FAQ_ITEMS = [
    {
        question: 'Is there a Bluebeam download for Mac?',
        answer: [
            'Not anymore. Bluebeam made Revu for Mac, with a final release (version 2.1) in December 2019, and Revu for Mac reached end of life on June 28, 2023. Current Bluebeam Revu is a Windows desktop application, and Bluebeam offers Bluebeam on web and mobile to its subscribers. Mac users who want the Windows desktop app often run it in a virtual machine such as Parallels.',
            'PDF Markup Tool is a browser-native alternative. Open it in Safari or Chrome on any Mac and start marking up PDFs immediately -- no download, no install, no Windows required. Your file stays on your Mac.',
        ],
    },
] as const;

/** Source for the Revu for Mac dates above (Bluebeam's own support site, checked 2026-11-01). */
const REVU_FOR_MAC_EOL_SOURCE = {
    href: 'https://support.bluebeam.com/legacy/resources/revu-for-mac-end-of-life-eol-announcement.html',
    label: 'Bluebeam Technical Support: Revu for Mac End of Life (EOL) effective June 28, 2023',
} as const;

export function BluebeamForMacPage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical }}>
            <main>
                <Hero
                    eyebrow="PDF markup for Mac"
                    title={<>Full PDF markup on your Mac.<br />Opens in your browser.</>}
                    lead="PDF Markup Tool runs entirely in Safari, Chrome, or Firefox on macOS -- no install, no Windows VM, no Parallels. Open a drawing, mark it up, measure to scale, and export a finished PDF, all on your Mac."
                    cta={<Button href="/" variant="primary" size="lg">Open a PDF</Button>}
                    chips={CHIPS}
                />
                <ContextNote text="Bluebeam Revu has not had a native macOS app since 2023. Mac users in AEC have been left looking for a capable, browser-native replacement -- one that handles real construction drawings without a Windows machine. PDF Markup Tool was built for exactly that workflow." />
                <FeatureGrid
                    sectionTitle="Built for Mac. Built for AEC."
                    sectionLead="Every tool you need for construction drawing markup -- in any browser on any Mac, with nothing uploaded and no account required."
                    cards={FEATURE_CARDS}
                />
                <FAQList items={FAQ_ITEMS} />
                <section className="mktg-context">
                    <div className="mktg-container">
                        <p className="mktg-context__note">
                            Source: <a href={REVU_FOR_MAC_EOL_SOURCE.href}>{REVU_FOR_MAC_EOL_SOURCE.label}</a>
                        </p>
                    </div>
                </section>
                <CtaBand
                    title="Ready to mark up on your Mac?"
                    lead="Open a PDF in your browser and start right now -- no download, no signup."
                    actions={
                        <>
                            <Button href="/" variant="primary" size="lg">Open a PDF</Button>
                            <Button href="/redline-pdf-drawing/" variant="ghost">
                                How to redline a PDF drawing
                            </Button>
                        </>
                    }
                />
                <section className="mktg-context">
                    <div className="mktg-container">
                        <p className="mktg-context__note">
                            Using Revu 20 on another device? See the <a href="/bluebeam-revu-20-end-of-life/">Bluebeam Revu 20 end-of-life timeline</a>.
                        </p>
                    </div>
                </section>
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="Free browser-native PDF markup for AEC" />
        </MarketingPage>
    );
}
